import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateAnnualWellnessSummaryPDF } from "@/knowmeno/annualWellnessSummary";
import { listLabUploads } from "@/lib/labs/labs.functions";
import { listHealthScreenings } from "@/lib/screenings/screenings.functions";

// ============================================================
// DEV TOOL — Preview MY Annual Wellness Summary (AWS)
// ============================================================
// Sibling of /dev/preview-my-report. Calls the standalone AWS
// generator with the signed-in user's real profile, lab uploads,
// and screenings. Same RLS-scoped server fns as the Care tab.
// ============================================================

const DEV_TOOLS_ENABLED = true;
const FOUNDER_EMAIL_RE = /^founders(\+[^@]+)?@(knowmeno|meno-iq)\.com$/i;
const USER_KEY = "menotrack_user";

export const Route = createFileRoute("/dev/preview-annual-summary")({
  component: PreviewAwsPage,
});

function PreviewAwsPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setEmail(data.user?.email ?? null);
      setLoading(false);
    });
  }, []);

  if (loading) return <Shell>Loading…</Shell>;
  if (!DEV_TOOLS_ENABLED) return <Shell>403 — dev tools disabled.</Shell>;
  if (!email || !FOUNDER_EMAIL_RE.test(email)) {
    return (
      <Shell>
        403 — not authorised.
        {email && <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Signed in as {email}</div>}
      </Shell>
    );
  }

  const handleGenerate = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const raw = window.localStorage.getItem(USER_KEY);
      if (!raw) throw new Error("No profile found in storage. Open the app first to hydrate.");
      const user = JSON.parse(raw);

      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error("No access token — please sign in again.");

      const [labUploads, screenings] = await Promise.all([
        listLabUploads({ data: { accessToken } }).catch((e) => { console.warn("labs fetch failed", e); return []; }),
        listHealthScreenings({ data: { accessToken } }).catch((e) => { console.warn("screenings fetch failed", e); return []; }),
      ]);

      await generateAnnualWellnessSummaryPDF(user, {
        labUploads: labUploads as any,
        screenings: screenings as any,
      });

      setMsg(`AWS opened in new window. Pulled ${labUploads.length} lab upload(s) and ${screenings.length} screening(s).`);
    } catch (e: any) {
      setMsg(`Error: ${e?.message || String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 8px" }}>
        Preview my Annual Wellness Summary
      </h1>
      <p style={{ color: "#6b6a64", fontSize: 14, lineHeight: 1.55, margin: "0 0 18px" }}>
        Generates the standalone AWS PDF using <strong style={{ color: "#2b2b28", fontWeight: 500 }}>your</strong>{" "}
        real profile, labs, and screenings (signed in as {email}). Empty
        sections show graceful empty-state notices.
      </p>
      <button
        onClick={handleGenerate}
        disabled={busy}
        style={{
          padding: "12px 22px",
          borderRadius: 12,
          background: busy ? "#9bbfae" : "#4B1F4F",
          color: "#fff",
          border: "none",
          fontSize: 14,
          fontWeight: 500,
          cursor: busy ? "wait" : "pointer",
        }}
      >
        {busy ? "Generating…" : "Preview my Annual Wellness Summary (PDF) →"}
      </button>
      {msg && (
        <p style={{ fontSize: 13, color: "#2b2b28", marginTop: 18, lineHeight: 1.55 }}>{msg}</p>
      )}
      <p style={{ fontSize: 12, color: "#888", marginTop: 18, lineHeight: 1.55 }}>
        Opens in a new window. Use your browser's <em>Save as PDF</em> from the print dialog to download.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: "#FAF8F3", padding: "60px 24px", fontFamily: "-apple-system, system-ui, sans-serif", color: "#2b2b28" }}>
      <div style={{ maxWidth: 560, margin: "0 auto", background: "#fff", padding: "32px 28px", borderRadius: 18, border: "1px solid #e8e6df" }}>
        {children}
      </div>
    </div>
  );
}
